import { component$, useSignal, $, type PropFunction } from "@builder.io/qwik";
import { LuUpload } from "@qwikest/icons/lucide";
import { cn } from "~/lib/cn";

interface FileUploadProps {
  onChange$: PropFunction<(files: File[]) => void>;
  isUploading?: boolean;
  progress?: number;
  accept?: string;
  maxSize?: number;
  recommendedDimensions?: { width: number; height: number };
  label?: string;
}

export const FileUpload = component$<FileUploadProps>(
  ({
    onChange$,
    isUploading = false,
    progress = 0,
    accept = "image/*",
    maxSize,
    recommendedDimensions,
    label = "Drag & drop a file here, or click to select one",
  }) => {
    const isDragActive = useSignal<boolean>(false);
    const fileInputRef = useSignal<HTMLInputElement>();

    const handleDragEnter = $((e: DragEvent) => {
      e.preventDefault();
      isDragActive.value = true;
    });

    const handleDragLeave = $((e: DragEvent) => {
      e.preventDefault();
      isDragActive.value = false;
    });

    const handleDragOver = $((e: DragEvent) => {
      e.preventDefault();
    });

    const handleFiles = $((files: File[]) => {
      if (files.length === 0) return;

      const file = files[0];

      // Check file size if maxSize is specified
      if (maxSize && file.size > maxSize) {
        console.error(
          `File size exceeds the maximum limit of ${Math.round(maxSize / (1024 * 1024))}MB`,
        );
        return;
      }

      onChange$(files);
    });

    const handleDrop = $((e: DragEvent) => {
      e.preventDefault();
      isDragActive.value = false;

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);
        handleFiles(files);
        e.dataTransfer.clearData();
      }
    });

    const handleFileInputChange = $((e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const files = Array.from(target.files);
        handleFiles(files);
      }
    });

    const handleClick = $(() => {
      fileInputRef.value?.click();
    });

    return (
      <div class="relative w-full">
        <div
          class={cn(
            "hover:text-primary flex w-full cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed p-4 pt-6 transition-colors duration-200 hover:border-purple-700",
            isDragActive.value
              ? "border-purple-700 bg-purple-50"
              : "border-input bg-white",
          )}
          onClick$={handleClick}
          onDragEnter$={handleDragEnter}
          onDragLeave$={handleDragLeave}
          onDragOver$={handleDragOver}
          onDrop$={handleDrop}
        >
          <LuUpload class="text-brand-700 h-10 w-10" />
          <p class="text-secondary mt-4 text-sm">{label}</p>
          {(accept || maxSize || recommendedDimensions) && (
            <div class="text-tertiary mt-2 flex flex-col gap-1 text-center text-xs">
              {accept && <p>Accepted formats: {accept.replace(/,/g, ", ")}</p>}
              {maxSize && (
                <p>Maximum size: {Math.round(maxSize / (1024 * 1024))}MB</p>
              )}
              {recommendedDimensions && (
                <p>
                  Recommended dimensions: {recommendedDimensions.width}x
                  {recommendedDimensions.height}px
                </p>
              )}
            </div>
          )}
        </div>

        <input
          type="file"
          accept={accept}
          ref={(el) => (fileInputRef.value = el)}
          class="hidden"
          onChange$={handleFileInputChange}
        />

        {isUploading && (
          <div class="absolute inset-0 flex flex-col items-center justify-center bg-white bg-opacity-75 backdrop-blur">
            <div class="h-2 w-3/4 overflow-hidden rounded bg-gray-200">
              <div
                class="bg-brand-700 h-full"
                style={{
                  width: `${progress}%`,
                  transition: "width 0.5s ease-out",
                }}
              />
            </div>
            <p class="mt-2 text-sm text-gray-600">{progress}%</p>
          </div>
        )}
      </div>
    );
  },
);
